import { useMemo } from 'react';
import { DailyReflection } from '@/types/reflection';
import { Button } from '@/components/ui/button';
import { startOfWeek, format, isThisWeek, subWeeks } from 'date-fns';

interface ReflectionHistoryProps {
  reflections: DailyReflection[];
  hasMore: boolean;
  onLoadMore: () => void;
}

function getWeekLabel(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  if (isThisWeek(date, { weekStartsOn: 1 })) return 'This Week';

  const lastWeekStart = startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 });
  const thisWeekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
  if (date >= lastWeekStart && date < thisWeekStart) return 'Last Week';

  const weekStart = startOfWeek(date, { weekStartsOn: 1 });
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  return `${format(weekStart, 'MMM d')} – ${format(weekEnd, 'MMM d')}`;
}

export const ReflectionHistory = ({
  reflections,
  hasMore,
  onLoadMore,
}: ReflectionHistoryProps) => {
  const grouped = useMemo(() => {
    const groups: { label: string; items: DailyReflection[] }[] = [];
    let currentLabel = '';

    for (const r of reflections) {
      const label = getWeekLabel(r.reflectionDate);
      if (label !== currentLabel) {
        currentLabel = label;
        groups.push({ label, items: [] });
      }
      groups[groups.length - 1].items.push(r);
    }

    return groups;
  }, [reflections]);

  if (reflections.length === 0) {
    return (
      <div className="text-center py-6">
        <p className="text-sm text-muted-foreground">
          Your reflection history will appear here. Start reflecting daily to build self-awareness.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 pt-2">
      {grouped.map((group) => (
        <div key={group.label}>
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
            {group.label}
          </h4>
          <div className="space-y-3">
            {group.items.map((r) => (
              <div
                key={r.id}
                className="rounded-lg border border-border/50 bg-muted/30 p-3 space-y-1.5"
              >
                <p className="text-xs text-muted-foreground">
                  {format(new Date(r.reflectionDate + 'T00:00:00'), 'EEEE, MMM d')}
                </p>
                {r.energized && (
                  <p className="text-sm">
                    <span className="font-medium text-emerald-500">Energized:</span>{' '}
                    {r.energized}
                  </p>
                )}
                {r.drained && (
                  <p className="text-sm">
                    <span className="font-medium text-orange-500">Drained:</span>{' '}
                    {r.drained}
                  </p>
                )}
                {r.changeAction && (
                  <p className="text-sm">
                    <span className="font-medium text-blue-500">Change:</span>{' '}
                    {r.changeAction}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      {hasMore && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onLoadMore}
          className="w-full text-muted-foreground"
        >
          Load more
        </Button>
      )}
    </div>
  );
};
